import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef
} from "react";
import {
  InboxContext,
  QuickFilterType
} from "../../pages/inbox/InboxTableProvider";
import WhatsAppMessageListToast from "./WhatsAppMessageListToast";
import CompanyService from "../../services/CompanyService";
import { WhatsAppButton } from "./WhatsAppButton";
import { MessageItem } from "./typings";
import { openWhatsappPanel } from "./utils/openWhatsappPanel";
import CounterMsgContext from "../../pages/shared/CounterMsgContext";
import SocketContext, {
  WhatsAppNotificationEvent
} from "../../pages/shared/SocketContext";
import { useSocketEvent } from "../../pages/shared/SocketContext";
import ActivityService from "../../services/ActivityService";

// Drag vertical del botón flotante (no persiste: al refrescar vuelve al lugar original)
const DEFAULT_BOTTOM = 40;
const BUTTON_SIZE = 58;
const DRAG_MARGIN = 8;
const DRAG_THRESHOLD = 3;

export const FloatingWhatsAppUI = ({ onEnable }: { onEnable?: () => void }) => {
  const { setRequest, response, refetch, request } = useContext(InboxContext);
  const { inboxCount, getCounterInbox } = useContext(CounterMsgContext);
  const { socket } = useContext(SocketContext);

  const [openSignal, setOpenSignal] = useState(0);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  const hasEnabledRef = useRef(false);
  const enableOnce = useCallback(() => {
    if (!hasEnabledRef.current) {
      hasEnabledRef.current = true;
      onEnable?.();
    }
  }, [onEnable]);

  const canUseWhatsapp = CompanyService.canUseWhatsappNotifications();

  // ----------------------- Mapear activity list -> mensajes (memo) -----------------------
  const messages: MessageItem[] = useMemo(() => {
    const items = response?.list || [];
    const arr = Array.isArray(items) ? items : [];
    return arr.map((it: any) => ({
      id: it.ID,
      title: it.Name ?? it.Title ?? "Mensaje",
      description: it.secureContent ?? it.Content ?? "",
      personId:
        it.PersonId ?? it.PersonID ?? it.personId ?? it.person?.PersonId,
      personName: it.Name ?? it.person?.Name,
      phone: it.Phone ?? it.person?.Phone,
      historyMessageId: it.Chat ? it.ID : undefined,
      isNewConversation: !it.Chat,
      updatedAt: it.updatedAt ?? it.UpdatedAt ?? it.createdAt ?? it.CreatedAt
    }));
  }, [response?.list]);

  // -------------------------- Filtro optimista de removidos --------------------------
  const visibleMessages = useMemo(
    () => messages.filter((m) => !dismissedIds.has(String(m.id))),
    [messages, dismissedIds]
  );

  useEffect(() => {
    // Cuando la API devuelve nueva lista, reseteamos los ocultados optimistas
    setDismissedIds(new Set());
  }, [response?.list]);

  // -------------------------- Filtros base del Inbox (WA no leídos) --------------------------
  useEffect(() => {
    setRequest((prev: any) => {
      if (
        prev?.quickfilter === QuickFilterType.Whatsapp &&
        prev?.read === false
      )
        return prev;
      return {
        ...(prev ?? {}),
        quickfilter: QuickFilterType.Whatsapp,
        read: false
      };
    });
  }, [setRequest]);

  // ------------------------------ Refresco coordinado --------------------------------
  const refreshUnread = useCallback(() => {
    getCounterInbox?.();
    refetch(); // refetch hace bump de _v en el provider -> fuerza nueva data
  }, [getCounterInbox, refetch]);

  // ---------------------------- Esperar evento una vez ----------------------------
  const onceSocket = useCallback(
    (event: string, timeoutMs = 1500): Promise<void> =>
      new Promise((resolve) => {
        if (!socket) return resolve();
        const handler = () => {
          socket.off(event, handler as any);
          clearTimeout(timer);
          resolve();
        };
        const timer = window.setTimeout(() => {
          socket.off(event, handler as any);
          resolve(); // fallback
        }, timeoutMs);
        socket.on(event, handler as any);
      }),
    [socket]
  );

  // ----------------------- Socket -> refrescar contador + lista -----------------------
  const handleCallReceived = useCallback(
    (payload: any) => {
      const isWhatsApp =
        payload?.Type === 6 ||
        payload?.type === 6 ||
        payload?.typeName === "Whatsapp";

      if (isWhatsApp) {
        enableOnce();
        refreshUnread();
      }
    },
    [refreshUnread, enableOnce]
  );

  const handleCallTaken = useCallback(
    (payload: any) => {
      const isWhatsApp =
        payload?.Type === 6 ||
        payload?.type === 6 ||
        payload?.typeName === "Whatsapp";
      if (isWhatsApp) {
        enableOnce();
        refreshUnread();
      }
    },
    [refreshUnread, enableOnce]
  );

  useSocketEvent(WhatsAppNotificationEvent.CallReceived, handleCallReceived);
  useSocketEvent(WhatsAppNotificationEvent.CallTaken, handleCallTaken);

  const countKey = useMemo(
    () => JSON.stringify(inboxCount ?? null),
    [inboxCount]
  );
  const debounceRef = useRef<number | null>(null);
  // true mientras no hayamos visto el primer valor real de inboxCount
  const isFirstInboxCountRef = useRef(true);

  useEffect(() => {
    // Ignorar el mount y la primera carga de inboxCount (no son cambios reales)
    if (isFirstInboxCountRef.current) {
      if (countKey !== "null") {
        isFirstInboxCountRef.current = false;
      }
      return;
    }

    if (!canUseWhatsapp) return;
    const isWhatsAppContext =
      request.quickfilter === QuickFilterType.Whatsapp ||
      request.quickfilter === QuickFilterType.WhatsappInbound;
    if (!isWhatsAppContext) return;

    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      refetch();
    }, 250) as unknown as number;

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [countKey, refetch, canUseWhatsapp, request.quickfilter]);

  // ------------------------- Drag vertical (solo eje Y) -------------------------
  const [bottom, setBottom] = useState(DEFAULT_BOTTOM);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ y: 0, bottom: DEFAULT_BOTTOM });
  const didDragRef = useRef(false);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      dragStartRef.current = { y: e.clientY, bottom };
      didDragRef.current = false;
      setIsDragging(true);
    },
    [bottom]
  );

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: PointerEvent) => {
      const delta = dragStartRef.current.y - e.clientY; // subir => mayor bottom
      if (Math.abs(delta) > DRAG_THRESHOLD) didDragRef.current = true;
      const max = Math.max(
        DRAG_MARGIN,
        window.innerHeight - BUTTON_SIZE - DRAG_MARGIN
      );
      const next = dragStartRef.current.bottom + delta;
      setBottom(Math.min(Math.max(next, DRAG_MARGIN), max));
    };
    const onEnd = () => setIsDragging(false);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onEnd);
    window.addEventListener("pointercancel", onEnd);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onEnd);
      window.removeEventListener("pointercancel", onEnd);
    };
  }, [isDragging]);

  if (!canUseWhatsapp) return null;

  // ------------------------------ Abrir conversación ------------------------------
  const openConversation = async (m: MessageItem) => {
    setIsLoading(true);
    try {
      // 1) ocultar optimista
      setDismissedIds((prev) => {
        const next = new Set(prev);
        next.add(String(m.id));
        return next;
      });

      // 2) marcar como leído
      if (m.id) {
        await ActivityService.setRead({ ids: [Number(m.id)], read: "1" });
      }

      // 3) abrir ventana persistente de WhatsApp (migrado desde FloatingPanel a WindowStore)
      await openWhatsappPanel({
        personId: (m.personId ?? m.id)?.toString(),
        isNewConversation: m.isNewConversation ?? !m.historyMessageId,
        historyMessageId: m.historyMessageId
          ? String(m.historyMessageId)
          : undefined
      });

      // 4) esperar confirmación del server para evitar carrera
      await onceSocket(WhatsAppNotificationEvent.CallTaken, 1500);

      // 5) refrescar contadores + lista
      refreshUnread();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <WhatsAppButton
        setOpenSignal={() => {
          // si veníamos de un arrastre, no abrir el panel
          if (didDragRef.current) {
            didDragRef.current = false;
            return;
          }
          enableOnce();
          setOpenSignal((n) => n + 1);
        }}
        isLoading={isLoading}
        onPointerDown={handlePointerDown}
        style={{
          bottom,
          cursor: isDragging ? "grabbing" : "pointer",
          touchAction: "none",
          userSelect: "none"
        }}
      />
      <WhatsAppMessageListToast
        items={visibleMessages}
        openSignal={openSignal}
        position="right"
        bottom={bottom}
        sideOffset={40}
        buttonSize={58}
        gap={12}
        autoHideMs={5000}
        onItemClick={openConversation}
      />
    </>
  );
};
